import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, LogOut, AlertTriangle, ShieldAlert, FileText, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, adminInput, Th, Td } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SecurePayment from "@/components/admin/SecurePayment";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PreflightModal({ rid, action, onClose, onDone }) {
  const [pf, setPf] = useState(null);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!rid) return;
    setPf(null); setOverride(false); setReason(""); setResult(null);
    api.get(`/admin/frontdesk/preflight/${rid}`, { params: { action } })
      .then((r) => setPf(r.data)).catch((e) => { toast.error(apiErr(e)); onClose(); });
  }, [rid, action]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/frontdesk/${action}/${rid}`, { override, reason });
      if (action === "checkout") { setResult(data); }
      else { toast.success(data.already ? "Already checked in." : "Guest checked in."); onDone(); onClose(); }
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const regen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/frontdesk/checkout/${rid}/regenerate-docs`); setResult((p) => ({ ...p, documents: data.documents, documents_ok: data.documents_ok })); toast.success("Documents regenerated."); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const hasBlockers = pf && pf.blockers && pf.blockers.length > 0;
  const canConfirm = pf && !pf.already && (!hasBlockers || (pf.can_override && override && reason.trim()));

  return (
    <Dialog open={!!rid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-surface border-white/10 max-w-lg" data-testid="preflight-modal">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2">
          {action === "checkin" ? <LogIn size={20} className="text-pool" /> : <LogOut size={20} className="text-gold" />}
          {action === "checkin" ? "Check-In Preflight" : "Checkout Preflight"}
        </DialogTitle></DialogHeader>
        {!pf ? <Loading /> : result ? (
          <div className="space-y-3" data-testid="checkout-result">
            <p className={`text-sm ${result.documents_ok ? "text-emerald-300" : "text-amber-300"} flex items-center gap-2`}>
              {result.documents_ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              Checkout complete{result.paid_in_full ? " · paid in full" : " · balance due"}. Documents {result.documents_ok ? "generated" : "partially failed"}.
            </p>
            <div className="space-y-1.5">
              {result.documents.map((d) => (
                <div key={d.type} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5" data-testid={`doc-${d.type}`}>
                  <span className="flex items-center gap-2 capitalize">{d.ok ? <CheckCircle2 size={14} className="text-emerald-300" /> : <XCircle size={14} className="text-red-300" />} {d.type.replace(/-/g, " ")}</span>
                  {d.ok ? <a href={`${BACKEND}${d.download}`} target="_blank" rel="noreferrer" className="text-pool text-xs inline-flex items-center gap-1" data-testid={`doc-dl-${d.type}`}><FileText size={12} /> Open</a>
                    : <span className="text-red-300 text-xs">Failed</span>}
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              {!result.documents_ok && <button onClick={regen} disabled={busy} className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-1" data-testid="regen-docs-btn"><RotateCcw size={13} /> Retry / Regenerate</button>}
              <button onClick={() => { onDone(); onClose(); }} className="btn-gold px-4 py-2 text-xs" data-testid="checkout-done-btn">Done</button>
            </div>
            {!result.paid_in_full && (
              <div className="pt-2" data-testid="frontdesk-collect-payment">
                <p className="text-xs text-amber-300 mb-2">Balance due — collect payment or send a secure payment link:</p>
                <SecurePayment rid={rid} />
              </div>
            )}
          </div>
        ) : pf.already ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This reservation is already {action === "checkin" ? "checked in" : "checked out"}.</p>
            <button onClick={onClose} className="btn-ghost-gold px-4 py-2 text-xs">Close</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm">
              <p className="font-medium text-gold">{pf.reservation.confirmation_number} · {pf.reservation.guest}</p>
              <p className="text-xs text-muted-foreground">{pf.reservation.experience} · {pf.reservation.accommodation_name || "No unit assigned"} · {fmtDate(pf.reservation.arrival_date)} → {fmtDate(pf.reservation.departure_date)}</p>
            </div>
            {hasBlockers && (
              <div className="rounded-lg border border-red-400/40 bg-red-400/5 p-3" data-testid="preflight-blockers">
                <p className="text-xs uppercase tracking-label text-red-300 flex items-center gap-1 mb-1.5"><ShieldAlert size={13} /> Blocking conditions</p>
                <ul className="text-sm space-y-1">{pf.blockers.map((b, i) => <li key={i} className="text-red-200">• {b}</li>)}</ul>
              </div>
            )}
            {pf.warnings && pf.warnings.length > 0 && (
              <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3" data-testid="preflight-warnings">
                <p className="text-xs uppercase tracking-label text-amber-300 flex items-center gap-1 mb-1.5"><AlertTriangle size={13} /> Warnings</p>
                <ul className="text-sm space-y-1">{pf.warnings.map((w, i) => <li key={i} className="text-amber-200">• {w}</li>)}</ul>
              </div>
            )}
            {!hasBlockers && (!pf.warnings || pf.warnings.length === 0) && <p className="text-sm text-emerald-300 flex items-center gap-2"><CheckCircle2 size={16} /> All checks passed.</p>}
            {hasBlockers && pf.can_override && (
              <div className="rounded-lg border border-white/10 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="override-toggle">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Manager override (bypass blockers)
                </label>
                {override && <textarea className={adminInput} rows={2} placeholder="Reason for override (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-reason" />}
              </div>
            )}
            {hasBlockers && !pf.can_override && <p className="text-xs text-red-300">A manager must override these blockers. You do not have override permission.</p>}
            <div className="flex gap-2">
              <button onClick={confirm} disabled={!canConfirm || busy} className="btn-gold px-5 py-2.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="preflight-confirm">
                {action === "checkin" ? "Confirm Check-In" : "Confirm Checkout"}
              </button>
              <button onClick={onClose} className="btn-ghost-gold px-5 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminFrontDesk() {
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [ops, setOps] = useState({});
  const [modal, setModal] = useState({ rid: null, action: "checkin" });
  const load = () => api.get("/admin/frontdesk/queue").then((r) => setQ(r.data)).catch((e) => { setQ(false); toast.error(apiErr(e)); });
  useEffect(() => { load(); api.get("/admin/ops/summary").then((r) => setOps(r.data.cards || {})).catch(() => {}); }, []);
  if (q === null) return <Loading />;
  if (!q) return <p className="text-muted-foreground">Front Desk access required.</p>;

  const Row = ({ r, action, label, icon: Ic }) => (
    <tr className="hover:bg-white/5" data-testid={`fd-row-${r.id}`}>
      <Td className="text-gold">{r.confirmation_number}</Td>
      <Td>{r.guest}</Td>
      <Td className="text-muted-foreground text-xs">{r.accommodation_name || "—"}</Td>
      <Td className="text-muted-foreground text-xs">{r.checkin_status || "—"}</Td>
      <Td className="text-right">
        <button onClick={() => setModal({ rid: r.id, action })} className="btn-ghost-gold px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`fd-${action}-${r.id}`}>
          <Ic size={13} /> {label}
        </button>
      </Td>
    </tr>
  );
  const Table = ({ rows, action, label, icon, empty }) => rows.length === 0 ? <p className="text-sm text-muted-foreground py-4">{empty}</p> : (
    <table className="w-full"><thead><tr><Th>Conf.</Th><Th>Guest</Th><Th>Unit</Th><Th>Check-In</Th><Th></Th></tr></thead>
      <tbody>{rows.map((r) => <Row key={r.id} r={r} action={action} label={label} icon={icon} />)}</tbody></table>
  );

  return (
    <div>
      <AdminHeading title="Front Desk" subtitle={`Arrivals, departures & guests in house · ${q.today}`} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {[["Pending Checkout", ops.pending_checkout, "/admin/reservations?filter=pending-checkout", "gold"],
          ["Arrived · Not Checked In", ops.arrived_not_checked_in, "/admin/reservations?filter=arrived-not-checked-in", "pool"],
          ["No Accommodation", ops.no_accommodation, "/admin/reservations?filter=no-accommodation", "red"],
          ["Rooms Not Ready", ops.rooms_not_ready, "/admin/rooms?group=not-ready", "red"],
          ["Active Maintenance Impact", ops.active_maintenance_impact, "/admin/reservations?filter=active-maintenance-impact", "gold"]].map(([l, v, to, a]) => (
          <button key={l} onClick={() => nav(to)} className="glass rounded-lg p-4 text-left border border-white/10 hover:border-pool/40 transition-colors" data-testid={`fd-card-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
            <p className="text-[10px] tracking-label uppercase text-muted-foreground">{l}</p>
            <p className={`font-serif text-3xl mt-1 ${a === "red" ? "text-red-300" : a === "pool" ? "text-pool" : "text-gold"}`}>{v ?? 0}</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title={`Arrivals — Check In (${q.arrivals.length})`}>
          <Table rows={q.arrivals} action="checkin" label="Check In" icon={LogIn} empty="No arrivals today." />
        </Panel>
        <Panel title={`Departures — Check Out (${q.departures.length})`}>
          <Table rows={q.departures} action="checkout" label="Check Out" icon={LogOut} empty="No departures today." />
        </Panel>
        <Panel title={`Currently In House (${q.in_house.length})`} className="lg:col-span-2">
          <Table rows={q.in_house} action="checkout" label="Check Out" icon={LogOut} empty="No guests in house." />
        </Panel>
      </div>
      <PreflightModal rid={modal.rid} action={modal.action} onClose={() => setModal({ rid: null, action: modal.action })} onDone={load} />
    </div>
  );
}
